/**
 * Каталог задач: карточки по темам в рекомендуемом порядке изучения
 * (docs/10-ui-ux.md, «Каталог»).
 *
 * Состояние фильтров живёт в адресной строке, чтобы ссылку на выборку можно
 * было сохранить и переслать. Статус задачи дублируется текстом рядом
 * со значком — цвет и форма не единственный носитель смысла.
 */

import { useSearchParams } from 'react-router'

import { useCurrentUser } from '@/api/auth'
import { useTasks, useTopics, type TaskListItem, type Topic } from '@/api/catalog'
import { strings } from '@/lib/strings'

import { TaskFiltersBar } from './TaskFiltersBar'
import { TaskRow } from './TaskRow'
import { hasAnyFilter, parseFilters } from './filters'

export function CatalogPage() {
  const [params] = useSearchParams()
  const filters = parseFilters(params)
  const { data: user } = useCurrentUser()

  const topics = useTopics()
  const tasks = useTasks(filters)

  const grouped = groupByTopic(tasks.data?.items ?? [])

  return (
    <section className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">{strings.catalog.title}</h1>

      <TaskFiltersBar
        filters={filters}
        topics={topics.data ?? []}
        tags={availableTags(tasks.data?.items ?? [])}
        canFilterByStatus={user != null}
      />

      {tasks.isPending && <p className="text-sm text-muted">{strings.catalog.loading}</p>}

      {tasks.isError && (
        <p role="alert" className="text-sm text-warning">
          {strings.catalog.loadFailed}
        </p>
      )}

      {tasks.isSuccess && grouped.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center">
          <p className="font-medium">{strings.catalog.empty}</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted">
            {hasAnyFilter(filters) ? strings.catalog.emptyHint : ''}
          </p>
        </div>
      )}

      {grouped.map(([topicSlug, items]) => {
        const topic = topics.data?.find((item) => item.slug === topicSlug)
        return (
          <section key={topicSlug} aria-labelledby={`topic-${topicSlug}`} className="space-y-2">
            <header className="flex items-baseline justify-between gap-4">
              <h2 id={`topic-${topicSlug}`} className="font-semibold">
                {items[0]?.topic.title ?? topicSlug}
              </h2>
              <p className="text-xs text-muted">{topicProgress(topic)}</p>
            </header>
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
              {items.map((task) => (
                <TaskRow key={task.slug} task={task} />
              ))}
            </ul>
          </section>
        )
      })}
    </section>
  )
}

function topicProgress(topic: Topic | undefined): string {
  if (topic === undefined) return ''
  return topic.tasks_solved === null
    ? strings.catalog.tasksTotal(topic.tasks_total)
    : strings.catalog.solvedOfTotal(topic.tasks_solved, topic.tasks_total)
}

/** Группировка сохраняет порядок сервера: он и есть порядок изучения. */
function groupByTopic(items: TaskListItem[]): [string, TaskListItem[]][] {
  const groups = new Map<string, TaskListItem[]>()
  for (const task of items) {
    const existing = groups.get(task.topic.slug)
    if (existing) existing.push(task)
    else groups.set(task.topic.slug, [task])
  }
  return [...groups.entries()]
}

function availableTags(items: TaskListItem[]): string[] {
  return [...new Set(items.flatMap((task) => task.tags))].sort((a, b) => a.localeCompare(b))
}
